import React, { Component, ErrorInfo, ReactNode } from "react";
import { t } from '@/i18n/t';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  public static getDerivedStateFromError(error: Error): State {
    console.error("🛑 Error Boundary caught error:", error);
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("🛑 Error Boundary - Full error details:");
    console.error("❌ Error:", error);
    console.error("📄 Error Info:", errorInfo);
    console.error("🔍 Component Stack:", errorInfo.componentStack);
    console.error("📚 Error Stack:", error.stack);
    
    this.setState({
      error,
      errorInfo
    });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-red-50">
          <div className="max-w-md mx-auto bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <span className="text-red-600 text-xl">⚠️</span>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 text-center mb-2">
              {t('common:x4.somethingWentWrong', 'Etwas ist schiefgelaufen')}
            </h1>
            <p className="text-gray-600 text-center mb-4">
              {t('common:x4.unexpectedError', 'Die Anwendung ist auf einen unerwarteten Fehler gestoßen.')}
            </p>
            
            {process.env.NODE_ENV === 'development' && (
              <details className="bg-gray-100 p-4 rounded text-sm mb-4">
                <summary className="cursor-pointer font-semibold">
                  Fehlerdetails (Development)
                </summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs overflow-auto">
                  <strong>Error:</strong> {this.state.error?.message}
                  {'\n\n'}
                  <strong>Stack:</strong>
                  {'\n'}
                  {this.state.error?.stack}
                  {'\n\n'}
                  <strong>Component Stack:</strong>
                  {'\n'}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
              >
                {t('common:x4.reloadPage', 'Seite neu laden')}
              </button>
              <button
                onClick={() => this.setState({ hasError: false })}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition-colors"
              >
                {'Erneut versuchen'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}